import styled from "@emotion/styled";
import { isNumber } from "lodash-es";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import type { FC, MouseEvent } from "react";
import { MDSText, MDSTextColor, MDSTextOverflow, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import type { SessionPartProps } from "@/mds/components/sessions/session-parts/session-part.types";
import { getSessionPartClientMeta } from "@/mds/components/sessions/session-parts/common/session-part-client-meta";
import { SessionClippedTextContent } from "@/mds/components/sessions/session-parts/common/session-clipped-text-content";
import { MDSSessionThreadRow } from "@/mds/components/sessions/session-parts/common/session-thread-row";
import {
  useIsLastPartInSessionGroup,
  useIsLastUserMessagePartInSessionHistory,
} from "@/mds/components/sessions/session-context/mds-session-context.hooks";
import { MDSSessionSentBadge } from "../session-sent-badge";
import { SessionSentBadgeColor } from "../session-sent-badge/session-sent-badge.types";
import { SessionEventKind } from "../session.types";
import type { WebClipV1SessionPart } from "../session.types";

type Props = SessionPartProps<WebClipV1SessionPart>;

const getTrimmedValue = (value: string | null | undefined): string | null => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : null;
};

const getWebClipLinkIdentity = (
  urlString: string | null
): { host: string; linkUrl: string | null } => {
  if (!urlString) {
    return { host: "Shared webpage", linkUrl: null };
  }

  try {
    const url = new URL(urlString);
    const host = url.hostname.startsWith("www.") ? url.hostname.slice(4) : url.hostname;
    const isWebProtocol = url.protocol === "http:" || url.protocol === "https:";
    return { host, linkUrl: isWebProtocol ? urlString : null };
  } catch {
    return { host: urlString, linkUrl: null };
  }
};

const getFaviconFallbackText = (host: string): string => {
  const firstHostComponent = host.split(".")[0]?.trim();
  return (firstHostComponent || "web").slice(0, 2).toLowerCase();
};

export const MDSSessionWebClipV1Part: FC<Props> = ({ part, context, config }) => {
  const isLastInGroup = useIsLastPartInSessionGroup({ part });
  const isLastUserMessage = useIsLastUserMessagePartInSessionHistory({ part });
  const meta = getSessionPartClientMeta(part);
  const completionPercentage = isNumber(meta?.completion_percentage)
    ? meta.completion_percentage
    : 1;
  const isPendingCompletion = completionPercentage < 1;

  const capturedWebsiteUrl = getTrimmedValue(part.captured_website_url);
  const capturedWebsiteTitle = getTrimmedValue(part.captured_website_title);
  const previewDescription = getTrimmedValue(part.preview_description);
  const { host, linkUrl } = useMemo(
    () => getWebClipLinkIdentity(capturedWebsiteUrl),
    [capturedWebsiteUrl]
  );
  const title = capturedWebsiteTitle ?? host;
  const faviconUrl = getTrimmedValue(part.preview_favicon_url);
  const faviconFallbackText = getFaviconFallbackText(host);
  const isIosShareExtensionWebClip =
    context.userActionSurface === "ios_share_extension" &&
    context.userActionInputModality === "web_clip" &&
    part.is_primary_clip !== false;

  const [failedImageUrls, setFailedImageUrls] = useState<ReadonlySet<string>>(new Set());
  const markImageUrlAsFailed = (url: string) => {
    setFailedImageUrls(previous => new Set(previous).add(url));
  };

  const thumbnailUrl =
    [
      part.preview_thumbnail_uploaded_file_url,
      part.preview_thumbnail_url,
      part.clipped_image_uploaded_file_url,
    ]
      .map(getTrimmedValue)
      .find((url): url is string => url !== null && !failedImageUrls.has(url)) ?? null;
  const canRenderFavicon = faviconUrl !== null && !failedImageUrls.has(faviconUrl);
  const threadReplyCount = config.threads ? (context.threadReplyCount ?? 0) : 0;
  const hasThreadReplies = threadReplyCount > 0;

  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (linkUrl && config.onClickLink?.(linkUrl, event.currentTarget, event) === true) {
      event.preventDefault();
    }
  };

  const richCardContent = (
    <CardContent>
      <CardTextContent>
        <CardTitle
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.Smedium}
          numberOfLines={2}
        >
          {title}
        </CardTitle>
        {previewDescription && (
          <CardDescription
            variant={MDSTextVariant.TextXs}
            color={MDSTextColor.Secondary}
            numberOfLines={2}
          >
            {previewDescription}
          </CardDescription>
        )}
        <CardSource title={capturedWebsiteUrl ?? undefined}>
          {canRenderFavicon ? (
            <Favicon
              data-test-id="web-clip-favicon"
              src={faviconUrl}
              alt=""
              onError={() => markImageUrlAsFailed(faviconUrl)}
            />
          ) : (
            <FaviconFallback aria-hidden="true">{faviconFallbackText}</FaviconFallback>
          )}
          <SourceLabel
            variant={MDSTextVariant.TextXs}
            color={MDSTextColor.Secondary}
            overflow={MDSTextOverflow.Ellipsis}
          >
            {host}
          </SourceLabel>
        </CardSource>
      </CardTextContent>
      <ThumbnailContainer>
        {thumbnailUrl ? (
          <Thumbnail
            data-test-id="web-clip-thumbnail"
            src={thumbnailUrl}
            alt=""
            onError={() => markImageUrlAsFailed(thumbnailUrl)}
          />
        ) : (
          <ThumbnailPlaceholder data-test-id="web-clip-thumbnail-placeholder" aria-hidden="true">
            <ThumbnailPlaceholderIcon>
              {canRenderFavicon ? (
                <PlaceholderFavicon
                  src={faviconUrl}
                  alt=""
                  onError={() => markImageUrlAsFailed(faviconUrl)}
                />
              ) : (
                faviconFallbackText
              )}
            </ThumbnailPlaceholderIcon>
          </ThumbnailPlaceholder>
        )}
      </ThumbnailContainer>
    </CardContent>
  );

  const isSentBadgeRenderable = isLastInGroup && isLastUserMessage && !hasThreadReplies;
  const loadingOverlay = (
    <AnimatePresence>
      {isPendingCompletion && (
        <LoadingOverlay
          data-test-id="web-clip-loading-overlay"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SmallSpinner
            animate={{ rotate: 360 }}
            transition={{ duration: 1, ease: "linear", repeat: Infinity }}
          />
        </LoadingOverlay>
      )}
    </AnimatePresence>
  );

  return (
    <MessageWrapper $isLastInGroup={isLastInGroup}>
      <Container eventKind={context.eventKind}>
        {isIosShareExtensionWebClip ? (
          <Card data-test-id="web-clip-card">
            {linkUrl ? (
              <CardLink
                href={linkUrl}
                target={config.openLinksInNewTab ? "_blank" : undefined}
                rel={config.openLinksInNewTab ? "noreferrer" : undefined}
                aria-label={`Open ${title}`}
                onClick={handleCardClick}
              >
                {richCardContent}
              </CardLink>
            ) : (
              richCardContent
            )}
            {loadingOverlay}
          </Card>
        ) : (
          <LegacyContent>
            <SessionClippedTextContent
              clippedTextContent={part.clipped_text_content}
              clippedRichTextContent={part.clipped_rich_text_content}
              isLastInGroup={isLastInGroup}
            />
            {!part.clipped_text_content && part.clipped_image_uploaded_file_url && (
              <LegacyWebClipImage
                src={part.clipped_image_uploaded_file_url}
                alt="Clipped web page"
              />
            )}
            {loadingOverlay}
          </LegacyContent>
        )}
      </Container>
      {hasThreadReplies && (
        <ThreadRowContainer>
          <MDSSessionThreadRow
            replyCount={threadReplyCount}
            onOpenThread={() => config.threads?.onOpenThread(context.eventId)}
            showUserAvatar={context.threadHasUserReply === true}
            showAgentAvatar={context.threadHasAgentReply === true}
            userPhotoUrl={config.threads?.userPhotoUrl}
          />
        </ThreadRowContainer>
      )}
      {isSentBadgeRenderable && (
        <MDSSessionSentBadge
          color={SessionSentBadgeColor.Green}
          isVisible={!isPendingCompletion}
          label="Sent to Mem"
        />
      )}
    </MessageWrapper>
  );
};

const MessageWrapper = styled.div<{
  $isLastInGroup: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  margin-bottom: ${({ $isLastInGroup, theme }) =>
    $isLastInGroup ? theme.spacing["0.5"] : theme.spacing["2"]};
`;

const Container = styled(MDSSessionPartContainer)<{ eventKind: SessionEventKind }>`
  position: relative;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 ? theme.spacing["1"] : "0"};
`;

const Card = styled.div`
  position: relative;
  width: min(100%, 345px);
  height: 128px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.preview};
  box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
`;

const CardLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
  color: inherit;
  text-decoration: none;
  outline: none;

  &:hover {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const CardContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
`;

const CardTextContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding: 14px 16px;
`;

/* Card text line-heights are tighter than the theme presets to match the
   web reference design. */
const CardTitle = styled(MDSText)`
  line-height: 18px;
  overflow-wrap: anywhere;
`;

const CardDescription = styled(MDSText)`
  margin-top: 6px;
  line-height: 15px;
  overflow-wrap: anywhere;
`;

const CardSource = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-top: auto;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const SourceLabel = styled(MDSText)`
  min-width: 0;
`;

const Favicon = styled.img`
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  object-fit: contain;
`;

const FaviconFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.surface.preview};
  font-size: 8px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const ThumbnailContainer = styled.div`
  width: 128px;
  height: 128px;
  flex: 0 0 128px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface.preview};

  @media (max-width: 420px) {
    width: 104px;
    flex-basis: 104px;
  }
`;

const Thumbnail = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface.previewHeader};
`;

const ThumbnailPlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surface.preview};
  box-shadow: 0 1px 2px rgb(0 0 0 / 4%);
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
`;

const PlaceholderFavicon = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  object-fit: contain;
`;

const LegacyContent = styled.div`
  position: relative;
  display: flex;
  width: min(100%, 360px);
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const LegacyWebClipImage = styled.img`
  width: 100%;
  height: 220px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  object-fit: cover;
  object-position: top;
`;

const ThreadRowContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => `${theme.spacing["1"]} ${theme.spacing["0.5"]} 0`};
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background-color: rgb(255 255 255 / 60%);
  backdrop-filter: blur(2px);
`;

const SmallSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.colors.icon.primary};
  mask-image: linear-gradient(
    to left,
    rgb(0 0 0 / 100%) 0%,
    rgb(0 0 0 / 60%) 65%,
    rgb(0 0 0 / 0%) 100%
  );
  will-change: transform;
`;
